// 🩺 치과 「오늘」 — 환자앱 「첫 방문 등록」 수신 카드 + 상세 모달 (2026-09-30 대표 승인, 배포 전 플래그 OFF)
// 계약: docs/계약_첫방문등록_서버_2026-09-30.md (listClinicIntakes / getClinicIntake / transcribeClinicIntake)
// 문구: docs/동의정본_첫방문등록_intake_2026-09-30.md §10-1 그대로
// 원칙:
//  - 목록에는 내용(이름 포함) 없음 — 서버 응답 자체가 그렇다. 상세는 모달 1곳(CF)에서만.
//  - 인쇄·복사·내보내기 버튼 없음, user-select: none, 열람 후 60초 무조작 시 자동 닫힘.
//  - 콘솔에 환자명·내용 기록 금지 — 오류 로그는 코드·intakeId 까지만.
//  - 대표·직원 모두 열람(계약 §4-2). 부모(ClinicToday)가 플래그 ON일 때만 이 컴포넌트를 렌더한다 → OFF면 호출 0.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ClipboardCheck } from 'lucide-react';
import {
    INTAKE_COMMON_FIELDS, INTAKE_OPTIONAL_FIELDS, INTAKE_NOTICE, INTAKE_CONFIRM_TEXT,
    intakeErrorMessage, formatIntakePhone, formatIntakeTime,
} from '../../utils/patientIntake';

const IDLE_MS = 60 * 1000;   // 열람 후 무조작 자동 닫힘

const call = (name, payload) => httpsCallable(getFunctions(undefined, 'asia-northeast3'), name)(payload || {});

function ClinicIntakeCard() {
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [listErr, setListErr] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(null);   // 열린 intakeId

    const loadList = useCallback(async () => {
        setListErr('');
        try {
            const r = await call('listClinicIntakes');
            const d = r?.data || {};
            setItems(Array.isArray(d.items) ? d.items : []);
            setCount(Number(d.count) || 0);
        } catch (e) {
            // 플래그 OFF(feature-off)는 정상 상태 — 카드 자체를 숨긴다. 그 외는 카드에 오류 문구
            if (e?.details?.code === 'feature-off') { setItems([]); setCount(0); }
            else {
                console.error('[첫 방문 등록] 목록 조회 실패:', e?.code || e?.message || e);
                setListErr(intakeErrorMessage(e, '목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
            }
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => { loadList(); }, [loadList]);

    if (!loaded) return null;
    if (!listErr && count === 0) return null;   // 대기 건 없으면 카드 생략

    return (
        <>
            <div style={S.card}>
                <div style={S.head}>
                    <div style={S.iconBox}><ClipboardCheck size={18} color="#fff" /></div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={S.title}>{`첫 방문 등록 ${count}건 · 옮겨 적기 대기`}</div>
                        <div style={S.notice}>{INTAKE_NOTICE}</div>
                    </div>
                    <button onClick={loadList} style={S.chip}>새로 고침</button>
                </div>
                {listErr && <div style={S.err}>{listErr}</div>}
                {items.length > 0 && (
                    <div style={S.rows}>
                        {items.map(it => (
                            <button key={it.intakeId} onClick={() => setOpen(it.intakeId)} style={S.row}>
                                <span style={S.rowTime}>{formatIntakeTime(it.sentAt)} 보냄</span>
                                <span style={{ ...S.badge, ...(it.status === 'viewed' ? S.badgeViewed : S.badgeSent) }}>
                                    {it.status === 'viewed' ? '열람함' : '새 등록'}
                                </span>
                                {it.hasOptional && <span style={S.sub}>추가 항목 있음</span>}
                                <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 12 }}>열기 ›</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {open && (
                <IntakeDetailModal
                    intakeId={open}
                    onClose={() => setOpen(null)}
                    onChanged={loadList}
                />
            )}
        </>
    );
}

// 상세 모달 — 열면 getClinicIntake(sent→viewed 전이). 60초 무조작 시 자동 닫힘.
function IntakeDetailModal({ intakeId, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);   // 2중 클릭 가드 (state 갱신 전 재진입 차단)
    const [done, setDone] = useState(null);   // { purgeAfter } 옮겨 적음 완료
    const idleTimer = useRef(null);

    // 무조작 타이머 — 모달 안 조작(마우스·키·터치)마다 리셋
    const armIdle = useCallback(() => {
        if (idleTimer.current) clearTimeout(idleTimer.current);
        idleTimer.current = setTimeout(() => onClose(), IDLE_MS);
    }, [onClose]);
    useEffect(() => {
        armIdle();
        return () => { if (idleTimer.current) clearTimeout(idleTimer.current); };
    }, [armIdle]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await call('getClinicIntake', { intakeId });
                if (!alive) return;
                setData(r?.data?.intake || null);
                if (!r?.data?.intake) setErr('전달본이 없습니다. 환자가 취소했거나 보관 기간이 지나 삭제된 건입니다.');
            } catch (e) {
                if (!alive) return;
                console.error('[첫 방문 등록] 상세 조회 실패:', e?.code || e?.message || e, intakeId);
                setErr(intakeErrorMessage(e, '내용을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [intakeId]);

    const transcribe = async () => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setErr('');
        try {
            const r = await call('transcribeClinicIntake', { intakeId });
            const d = r?.data || {};
            setDone({ purgeAfter: d.purgeAfter || null });
            setConfirming(false);
            if (typeof onChanged === 'function') onChanged();
        } catch (e) {
            console.error('[첫 방문 등록] 옮겨 적음 실패:', e?.code || e?.message || e, intakeId);
            setErr(intakeErrorMessage(e, '옮겨 적음 처리를 하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    const requested = Array.isArray(data?.requestedFields) ? data.requestedFields : [];
    const optional = data?.optional || {};
    // 정본 §10-1: 요청하지 않은 항목 행은 표시하지 않음(빈 값 "없음" 표시 금지). 요청했지만 값 없는 항목도 행 생략.
    const optionalRows = INTAKE_OPTIONAL_FIELDS.filter(f => requested.includes(f.key) && optional[f.key]);

    return (
        <div style={S.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div
                style={S.modal}
                role="dialog"
                aria-label="첫 방문 등록"
                onMouseMove={armIdle} onKeyDown={armIdle} onClick={armIdle} onTouchStart={armIdle}
                onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()}
            >
                <div style={S.mHead}>
                    <div style={S.mTitle}>첫 방문 등록</div>
                    <button onClick={onClose} style={S.btnClose} aria-label="닫기">×</button>
                </div>
                <div style={S.mNotice}>{INTAKE_NOTICE}</div>

                {loading && <div style={S.mEmpty}>불러오는 중…</div>}
                {!loading && err && !data && <div style={S.err}>{err}</div>}

                {data && !done && (
                    <>
                        <div style={S.mMeta}>
                            {formatIntakeTime(data.sentAt)} 보냄
                            {data.purgeAfter ? ` · ${formatIntakeTime(data.purgeAfter)}까지 보관` : ''}
                        </div>
                        <table style={S.table}>
                            <tbody>
                                {INTAKE_COMMON_FIELDS.map(f => (
                                    <tr key={f.key}>
                                        <th style={S.th}>{f.label}</th>
                                        <td style={S.td}>{f.key === 'phone' ? formatIntakePhone(data.common?.[f.key]) : (data.common?.[f.key] || '')}</td>
                                    </tr>
                                ))}
                                {optionalRows.map(f => (
                                    <tr key={f.key}>
                                        <th style={S.th}>{f.label}</th>
                                        <td style={S.td}>{optional[f.key]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {err && <div style={S.err}>{err}</div>}

                        {confirming ? (
                            <div style={S.confirmBox}>
                                <div style={S.confirmText}>{INTAKE_CONFIRM_TEXT}</div>
                                <div style={S.btnRow}>
                                    <button onClick={() => setConfirming(false)} disabled={busy} style={S.btnGhost}>돌아가기</button>
                                    <button onClick={transcribe} disabled={busy} style={{ ...S.btnP, opacity: busy ? 0.6 : 1 }}>{busy ? '처리 중…' : '옮겨 적음'}</button>
                                </div>
                            </div>
                        ) : (
                            <div style={S.btnRow}>
                                <button onClick={onClose} style={S.btnGhost}>닫기</button>
                                {data.status !== 'transcribed' && (
                                    <button onClick={() => setConfirming(true)} style={S.btnP}>옮겨 적음</button>
                                )}
                            </div>
                        )}
                    </>
                )}

                {done && (
                    <>
                        <div style={S.doneBox}>{`옮겨 적음 · ${formatIntakeTime(done.purgeAfter)}에 삭제`}</div>
                        <div style={S.btnRow}>
                            <button onClick={onClose} style={S.btnGhost}>닫기</button>
                        </div>
                    </>
                )}

                {!loading && err && !data && (
                    <div style={S.btnRow}>
                        <button onClick={onClose} style={S.btnGhost}>닫기</button>
                    </div>
                )}
            </div>
        </div>
    );
}

const S = {
    card: { background: '#fff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 16px', marginBottom: 16, userSelect: 'none' },
    head: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    iconBox: { width: 36, height: 36, borderRadius: 10, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { fontSize: 14.5, fontWeight: 700, color: '#0f172a' },
    notice: { fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.5 },
    chip: { padding: '7px 13px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    err: { background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 10 },
    rows: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 },
    row: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: '#334155' },
    rowTime: { fontWeight: 600 },
    badge: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' },
    badgeSent: { background: '#dbeafe', color: '#1d4ed8' },
    badgeViewed: { background: '#f1f5f9', color: '#64748b' },
    sub: { fontSize: 11.5, color: '#94a3b8' },

    overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: '18px 20px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', userSelect: 'none', WebkitUserSelect: 'none' },
    mHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
    mTitle: { fontSize: 16, fontWeight: 800, color: '#0f172a' },
    btnClose: { background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: 4 },
    mNotice: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, lineHeight: 1.55, marginBottom: 12 },
    mMeta: { fontSize: 12, color: '#64748b', marginBottom: 8 },
    mEmpty: { padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', width: 110, fontSize: 12.5, fontWeight: 700, color: '#64748b', padding: '9px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', whiteSpace: 'nowrap' },
    td: { fontSize: 13.5, color: '#0f172a', padding: '9px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 },
    confirmBox: { marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' },
    confirmText: { fontSize: 13, color: '#334155', lineHeight: 1.6, marginBottom: 10 },
    doneBox: { marginTop: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 10, padding: '12px 14px', fontSize: 13.5, fontWeight: 700 },
    btnRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
    btnGhost: { padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    btnP: { padding: '9px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
};

export default ClinicIntakeCard;
